// src/components/pipelines/overlays/TopBar.jsx - Floating top bar for flow canvas
import React, { useState } from 'react';
import { Button } from '../../ui/button.tsx';
import { Input } from '../../ui/input.tsx';
import { Badge } from '../../ui/badge.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog.tsx';
import { Eye, EyeOff, Check, Plus, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu.tsx';

/**
 * TopBar - Floating top bar overlay for flow canvas
 *
 * Props:
 * - flows: array of available flows
 * - currentFlow: currently selected flow object
 * - onFlowChange: callback when flow is selected
 * - onCreateFlow: callback when create new flow is clicked
 * - pipelines: array of pipelines for adding kanban nodes
 * - onAddNode: callback when adding a node (type, subtype, pipelineId)
 */
const TopBar = ({
  flows = [],
  currentFlow = null,
  onFlowChange,
  onCreateFlow,
  pipelines = [],
  onAddNode
}) => {
  const [showNewFlowDialog, setShowNewFlowDialog] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDescription, setNewFlowDescription] = useState('');

  const handleCreateFlow = () => {
    if (newFlowName.trim()) {
      onCreateFlow?.({
        name: newFlowName,
        description: newFlowDescription
      });
      setShowNewFlowDialog(false);
      setNewFlowName('');
      setNewFlowDescription('');
    }
  };

  const handleToggleStatus = () => {
    // TODO: Implement toggle status
    console.log('Toggle status');
  };

  return (
    <>
      {/* Floating top bar */}
      <div className="absolute top-4 left-4 right-4 z-50 flex items-center gap-3">
        <div className="flex-1 flex items-center gap-3 bg-slate-900/80 backdrop-blur-md border border-slate-700/50 rounded-lg px-4 py-2.5 shadow-xl">
          {/* Flow Selector */}
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Select
              value={currentFlow?.id}
              onValueChange={(value) => {
                if (value === 'new') {
                  setShowNewFlowDialog(true);
                } else {
                  const flow = flows.find(f => f.id === value);
                  onFlowChange?.(flow);
                }
              }}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
                <SelectValue placeholder="Select a flow..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {flows.map((flow) => (
                  <SelectItem
                    key={flow.id}
                    value={flow.id}
                    className="text-slate-100 focus:bg-slate-700"
                  >
                    <div className="flex items-center gap-2">
                      <span>{flow.name}</span>
                      {flow.total_customers > 0 && (
                        <span className="text-xs text-slate-400">
                          ({flow.total_customers})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
                <SelectItem
                  value="new"
                  className="text-purple-300 focus:bg-slate-700 font-medium"
                >
                  + Create New Flow
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="h-6 w-px bg-slate-700" />

          {/* Status Badge */}
          {currentFlow && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleStatus}
              className="gap-2"
            >
              <Badge
                variant={currentFlow.is_active ? "default" : "secondary"}
                className="cursor-pointer"
              >
                {currentFlow.is_active ? (
                  <><Eye className="w-3 h-3 mr-1" /> Active</>
                ) : (
                  <><EyeOff className="w-3 h-3 mr-1" /> Inactive</>
                )}
              </Badge>
            </Button>
          )}

          <div className="flex-1" />

          {/* Add Node Dropdown */}
          {currentFlow && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    Add Node
                    <ChevronDown className="w-3 h-3 ml-1.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-slate-900 border-slate-700" align="end">
                  <DropdownMenuLabel className="text-slate-300">Node Types</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem
                    className="text-slate-200 focus:bg-slate-800 cursor-pointer"
                    onClick={() => onAddNode?.('entry')}
                  >
                    <span className="mr-2">🎯</span>
                    Entry Point
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-slate-200 focus:bg-slate-800 cursor-pointer"
                    onClick={() => onAddNode?.('exit')}
                  >
                    <span className="mr-2">🏁</span>
                    Exit Point
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-slate-200 focus:bg-slate-800 cursor-pointer"
                    onClick={() => onAddNode?.('wait')}
                  >
                    <span className="mr-2">⏱️</span>
                    Wait
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-slate-200 focus:bg-slate-800 cursor-pointer"
                    onClick={() => onAddNode?.('logicGate', 'AND')}
                  >
                    <span className="mr-2">◆</span>
                    AND Gate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-slate-200 focus:bg-slate-800 cursor-pointer"
                    onClick={() => onAddNode?.('logicGate', 'OR')}
                  >
                    <span className="mr-2">◇</span>
                    OR Gate
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuLabel className="text-slate-300">📋 Kanban Boards</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-700" />

                  <div className="max-h-48 overflow-y-auto">
                    {pipelines.slice(0, 5).map((pipeline) => (
                      <DropdownMenuItem
                        key={pipeline.id}
                        className="text-slate-200 focus:bg-slate-800 cursor-pointer"
                        onClick={() => onAddNode?.('kanban', null, pipeline.id, pipeline)}
                      >
                        <span className="mr-2">📋</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{pipeline.name}</div>
                          <div className="text-xs text-slate-500">
                            {pipeline.stage_count || 0} stages
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      className="text-purple-300 focus:bg-slate-800 cursor-pointer border-t border-slate-700 mt-1"
                      onClick={() => onAddNode?.('kanban', null, null, null, true)}
                    >
                      <Plus className="w-3 h-3 mr-2" />
                      Create New Pipeline
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="h-6 w-px bg-slate-700" />
            </>
          )}

          {/* Auto-save indicator */}
          {currentFlow && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>All changes saved</span>
            </div>
          )}
        </div>
      </div>

      {/* Create New Flow Dialog */}
      <Dialog open={showNewFlowDialog} onOpenChange={setShowNewFlowDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Create New Flow</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new automation flow. You can add pipelines and logic gates after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">
                Flow Name
              </label>
              <Input
                placeholder="e.g., Customer Lifecycle"
                value={newFlowName}
                onChange={(e) => setNewFlowName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">
                Description (Optional)
              </label>
              <Input
                placeholder="Describe this automation flow..."
                value={newFlowDescription}
                onChange={(e) => setNewFlowDescription(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowNewFlowDialog(false)}
              className="text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFlow}
              disabled={!newFlowName.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Create Flow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TopBar;
